import React, { useState, useEffect } from 'react';
import {
  X, Mail, Phone, Calendar, Tag, MessageCircle, DollarSign,
  FileText, Shield, Megaphone, Paperclip, ExternalLink,
  TrendingUp, Clock, User, ChevronRight
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  lead: 'bg-brand-green', prospect: 'bg-brand-green', qualified: 'bg-violet-500',
  negotiation: 'bg-amber-500', closed_won: 'bg-emerald-500', closed_lost: 'bg-red-500', inquiry: 'bg-gray-500',
};
const statusLabels = {
  lead: 'Lead', prospect: 'Prospecto', qualified: 'Calificado',
  negotiation: 'Negociacion', closed_won: 'Ganado', closed_lost: 'Perdido', inquiry: 'Consulta',
};

export const CustomerProfile = ({ customerId, onClose, onNavigate }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customerId) return;
    setLoading(true);
    axios.get(`${API}/api/customers/${customerId}/profile`)
      .then(res => setProfile(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [customerId]);

  if (!customerId) return null;

  return (
    <AnimatePresence>
      <motion.div
        data-testid="customer-profile-overlay"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          data-testid="customer-profile-card"
          initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 350 }}
          className="w-full max-w-2xl max-h-[85vh] bg-card rounded-2xl border border-white/20 shadow-2xl overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : profile ? (
            <>
              {/* Banner Header */}
              <div className="relative h-28 bg-primary">
                <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors">
                  <X className="w-4 h-4" />
                </button>
                <div className="absolute -bottom-8 left-6">
                  <div className="w-16 h-16 rounded-xl bg-primary flex items-center justify-center text-2xl font-bold text-white border-4 border-card shadow-lg">
                    {profile.customer.name?.charAt(0) || '?'}
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              <div className="pt-10 px-6 pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold">{profile.customer.name}</h2>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={cn("text-xs text-white", statusColors[profile.customer.status])}>
                        {statusLabels[profile.customer.status] || profile.customer.status}
                      </Badge>
                      {profile.campaign && (
                        <Badge variant="outline" className="text-xs gap-1">
                          <Megaphone className="w-3 h-3" />
                          {profile.campaign.name}
                        </Badge>
                      )}
                    </div>
                  </div>
                  {profile.customer.value > 0 && (
                    <div className="text-right">
                      <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">${Number(profile.customer.value).toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Valor</p>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-3 mt-3">
                  {profile.customer.email && (
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Mail className="w-3.5 h-3.5" /> {profile.customer.email}
                    </div>
                  )}
                  {profile.customer.phone && (
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Phone className="w-3.5 h-3.5" /> {profile.customer.phone}
                    </div>
                  )}
                  {profile.customer.source && (
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Tag className="w-3.5 h-3.5" /> {profile.customer.source}
                    </div>
                  )}
                </div>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-5 gap-2 px-6 py-3">
                {[
                  { label: 'Chats', value: profile.stats.total_chats, icon: MessageCircle, color: 'text-brand-deep dark:text-brand-green' },
                  { label: 'Mensajes', value: profile.stats.total_messages, icon: MessageCircle, color: 'text-brand-deep dark:text-brand-green' },
                  { label: 'Facturas', value: profile.invoices.length, icon: FileText, color: 'text-amber-700 dark:text-amber-400' },
                  { label: 'Escrow', value: profile.escrow_transactions.length, icon: Shield, color: 'text-emerald-700 dark:text-emerald-400' },
                  { label: 'Archivos', value: profile.stats.total_files, icon: Paperclip, color: 'text-brand-deep dark:text-brand-deep dark:text-brand-green' },
                ].map((s, i) => (
                  <div key={`stat-${i}`} className="text-center p-2 bg-muted/30 rounded-lg">
                    <s.icon className={cn("w-4 h-4 mx-auto mb-0.5", s.color)} />
                    <p className="text-lg font-bold">{s.value}</p>
                    <p className="text-[10px] text-muted-foreground">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Scrollable sections */}
              <ScrollArea className="h-[35vh] px-6 pb-4">
                <div className="space-y-4">
                  {/* Notes */}
                  {profile.customer.notes && (
                    <div className="p-3 bg-muted/30 rounded-xl">
                      <p className="text-xs font-semibold text-muted-foreground mb-1">Notas</p>
                      <p className="text-sm">{profile.customer.notes}</p>
                    </div>
                  )}

                  {/* Chats */}
                  {profile.chats.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground mb-2">Conversaciones</p>
                      <div className="space-y-1">
                        {profile.chats.map(chat => (
                          <button key={chat.id} onClick={() => onNavigate && onNavigate('inbox')}
                            className="w-full flex items-center gap-3 p-2.5 bg-white/5 hover:bg-white/10 rounded-xl text-left transition-all group">
                            <MessageCircle className="w-4 h-4 text-brand-deep dark:text-brand-green" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm truncate">{chat.last_message || 'Chat'}</p>
                              <p className="text-[10px] text-muted-foreground">{chat.platform}</p>
                            </div>
                            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Invoices */}
                  {profile.invoices.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground mb-2">Facturas</p>
                      <div className="space-y-1">
                        {profile.invoices.map(inv => (
                          <button key={inv.id} onClick={() => onNavigate && onNavigate('invoices')}
                            className="w-full flex items-center gap-3 p-2.5 bg-white/5 hover:bg-white/10 rounded-xl text-left transition-all group">
                            <FileText className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                            <div className="flex-1">
                              <p className="text-sm">#{inv.invoice_number || inv.id?.slice(0, 8)}</p>
                              <p className="text-[10px] text-muted-foreground">{inv.status}</p>
                            </div>
                            <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">${Number(inv.total || 0).toLocaleString()}</span>
                            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Escrow */}
                  {profile.escrow_transactions.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground mb-2">Transacciones Escrow</p>
                      <div className="space-y-1">
                        {profile.escrow_transactions.map(e => (
                          <button key={e.id} onClick={() => onNavigate && onNavigate('escrow')}
                            className="w-full flex items-center gap-3 p-2.5 bg-white/5 hover:bg-white/10 rounded-xl text-left transition-all group">
                            <Shield className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
                            <div className="flex-1">
                              <p className="text-sm">{e.description || 'Escrow'}</p>
                              <p className="text-[10px] text-muted-foreground">{e.status}</p>
                            </div>
                            <span className="text-sm font-semibold">${Number(e.amount || 0).toLocaleString()}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Files */}
                  {profile.files.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground mb-2">Archivos</p>
                      <div className="flex flex-wrap gap-2">
                        {profile.files.map(f => (
                          <a key={f.id} href={`${API}${f.url}`} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg text-xs transition-all">
                            <Paperclip className="w-3 h-3" />
                            <span className="truncate max-w-[120px]">{f.original_filename}</span>
                            <ExternalLink className="w-3 h-3 text-muted-foreground" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Timeline */}
                  <div className="p-3 bg-muted/20 rounded-xl">
                    <p className="text-xs font-semibold text-muted-foreground mb-2">Timeline</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="w-3.5 h-3.5" />
                      <span>Primer contacto: {profile.stats.first_contact ? new Date(profile.stats.first_contact).toLocaleDateString() : 'N/A'}</span>
                    </div>
                    {profile.stats.last_interaction && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <TrendingUp className="w-3.5 h-3.5" />
                        <span>Ultima interaccion: {new Date(profile.stats.last_interaction).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </ScrollArea>
            </>
          ) : (
            <div className="flex items-center justify-center h-48 text-muted-foreground">
              <p>Cliente no encontrado</p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
